/**
 * Painel Jurídico (spec 029): Termos de Uso, Política de Privacidade e o termo
 * do Trabalhe Conosco. O texto mora na API, versionado; o front só renderiza.
 */

export type LegalDocumentType = "terms-of-use" | "privacy-policy" | "recruitment";

export const LEGAL_DOCUMENT_TYPES: LegalDocumentType[] = [
  "terms-of-use",
  "privacy-policy",
  "recruitment",
];

/** Nome curto (painel, checkbox) e a página pública de cada documento. */
export const LEGAL_DOCUMENTS: Record<
  LegalDocumentType,
  { label: string; acceptLabel: string; route: string }
> = {
  "terms-of-use": {
    label: "Termos de Uso",
    acceptLabel: "os Termos de Uso",
    route: "/termos-de-uso",
  },
  "privacy-policy": {
    label: "Política de Privacidade",
    acceptLabel: "a Política de Privacidade",
    route: "/privacidade",
  },
  recruitment: {
    label: "Termo do Trabalhe Conosco",
    acceptLabel: "o Termo de Privacidade do processo seletivo",
    route: "/trabalhe-conosco/privacidade",
  },
};

export function isLegalDocumentType(value: string): value is LegalDocumentType {
  return (LEGAL_DOCUMENT_TYPES as string[]).includes(value);
}

/** Uma versão publicada, como a leitura pública devolve. */
export interface LegalVersion {
  type: LegalDocumentType;
  version: string;
  title: string;
  markdown: string;
  /** `YYYY-MM-DD`. */
  effectiveDate: string;
  requiresReacceptance: boolean;
  changeNote?: string;
  publishedAt: string;
}

/** Linha do histórico: a versão sem o texto. */
export type LegalVersionSummary = Omit<LegalVersion, "markdown">;

/** Documento que a pessoa ainda precisa aceitar (modal de aceite). */
export interface PendingLegalDocument {
  type: LegalDocumentType;
  title: string;
  version: string;
  requiredVersion: string;
}

// ── Painel da gerente ─────────────────────────────────────────────────

export interface LegalDocumentOverview {
  type: LegalDocumentType;
  title: string;
  publishedVersion: string | null;
  requiredVersion: string | null;
  effectiveDate: string | null;
  hasDraft: boolean;
  draftUpdatedAt: string | null;
  pendingCount: number;
}

export interface LegalDraft {
  type: LegalDocumentType;
  title: string;
  markdown: string;
  basedOnVersion: string | null;
  /** `false` = ainda não salvo (é a vigente aberta para edição). */
  saved: boolean;
  updatedAt: string | null;
}

export interface PublishLegalPayload {
  effectiveDate: string;
  requiresReacceptance: boolean;
  changeNote?: string;
}

export interface LegalAcceptances {
  type: LegalDocumentType;
  requiredVersion: string | null;
  accepted: {
    userId: string;
    name: string;
    role: string;
    version: string;
    acceptedAt: string;
  }[];
  pending: { userId: string; name: string; role: string }[];
}

// ── Regras puras (mesmas do backend) ──────────────────────────────────

/** Negativo se `a` < `b`. Número a número: `1.10` vem depois de `1.9`. */
export function compareVersions(a: string, b: string): number {
  const [aMajor, aMinor] = parse(a);
  const [bMajor, bMinor] = parse(b);
  return aMajor !== bMajor ? aMajor - bMajor : aMinor - bMinor;
}

/**
 * A versão que a publicação vai criar: com novo aceite sobe a maior, sem sobe
 * a menor; a primeira é `1.0`. O backend decide — isto é só a prévia do modal.
 */
export function nextVersion(
  current: string | null,
  requiresReacceptance: boolean,
): string {
  if (!current) return "1.0";
  const [major, minor] = parse(current);
  return requiresReacceptance ? `${major + 1}.0` : `${major}.${minor + 1}`;
}

const ROLE_LABELS: Record<string, string> = {
  student: "Aluno",
  teacher: "Professora",
  manager: "Gerente",
  candidate: "Candidato",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/** `2026-10-24` → `24 de outubro de 2026`, sem passar por `Date` (evita fuso). */
export function formatLongDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return date;
  return `${day} de ${MONTHS[month - 1]} de ${year}`;
}

function parse(version: string): [number, number] {
  const [major, minor] = version.split(".").map((part) => Number(part) || 0);
  return [major ?? 0, minor ?? 0];
}
